import React, { Dispatch, SetStateAction, useState } from 'react';

import { RadioOrSelectOption } from '../../../../../types/common.types';
import { Member, Post } from '../../../../../types/entity.types';

import { APPROVED, DECLINED } from '../../../../../utils/constants';
import { base64ToImage } from '../../../../../utils/util';

import ProgrammeLayout from '../../../../../layouts/programme/ProgrammeLayout';
import Dropdown from '../../../../common/input/dropdown/Dropdown';

import TribePostContainer from '../../../../../containers/programme/tribe/tribe-post/TribePost.container';

import { ReactComponent as ArrowIcon } from '../../../../../resources/icons/arrow-single.svg';
import { ReactComponent as SearchIcon } from '../../../../../resources/icons/search.svg';
import { ReactComponent as DotsIcon } from '../../../../../resources/icons/three-dots.svg';
import { ReactComponent as UserIcon } from '../../../../../resources/icons/user.svg';

import './tribe-approvals.scss';

type Props = {
  status: string;
  posts?: Post[];
  members?: Member[];
  filter: RadioOrSelectOption;
  setFilter: Dispatch<SetStateAction<RadioOrSelectOption>>;
  viewApprovalsForUser: (userReference: string, callbackFn: () => void) => void;
  updateAllPosts: (userReference: string, status: string, callbackFn: () => void) => void;
  updateAllComments: (userReference: string, status: string, callbackFn: () => void) => void;
  navigateBack: () => void;
};

const TribeApprovals = ({
  status,
  posts = [],
  members = [],
  filter,
  setFilter,
  viewApprovalsForUser,
  updateAllPosts,
  updateAllComments,
  navigateBack
}: Props) => {
  const [searchString, setSearchString] = useState('');
  const [selectedSystemUserExternalReference, setSelectedSystemUserExternalReference] = useState<string | undefined>(
    undefined
  );

  const filterOptions = [
    { label: 'All Awaiting Approval', value: 'all:pending approval' },
    { label: 'All Posts Awaiting Approval', value: 'post:pending approval' },
    { label: 'All Comments Awaiting Approval', value: 'comment:pending approval' },
    { label: 'All Approved', value: 'all:approved' },
    { label: 'All Approved Posts', value: 'post:approved' },
    { label: 'All Approved Comments', value: 'comment:approved' },
    { label: 'All Declined', value: 'all:declined' },
    { label: 'All Declined Posts', value: 'post:declined' },
    { label: 'All Declined Comments', value: 'comment:declined' }
  ];

  const handleMemberClick = (systemUserExternalReference: string) => {
    if (systemUserExternalReference === selectedSystemUserExternalReference) {
      setSelectedSystemUserExternalReference(undefined);
    } else {
      setSelectedSystemUserExternalReference(systemUserExternalReference);
    }
  };

  const handleApproveAllPostsClick = () => {
    updateAllPosts(selectedSystemUserExternalReference ?? '', APPROVED, () =>
      setSelectedSystemUserExternalReference(undefined)
    );
  };

  const handleDeclineAllPostsClick = () => {
    updateAllPosts(selectedSystemUserExternalReference ?? '', DECLINED, () =>
      setSelectedSystemUserExternalReference(undefined)
    );
  };

  const handleApproveAllCommentsClick = () => {
    updateAllComments(selectedSystemUserExternalReference ?? '', APPROVED, () =>
      setSelectedSystemUserExternalReference(undefined)
    );
  };

  const handleDeclineAllCommentsClick = () => {
    updateAllComments(selectedSystemUserExternalReference ?? '', DECLINED, () =>
      setSelectedSystemUserExternalReference(undefined)
    );
  };

  const handleViewAllClick = () => {
    viewApprovalsForUser(selectedSystemUserExternalReference ?? '', () =>
      setSelectedSystemUserExternalReference(undefined)
    );
  };

  const renderMembers = () => {
    return members.map((member, index) => {
      const {
        memberName,
        systemUserExternalReference = '',
        postsPendingApproval = 0,
        commentsPendingApproval = 0,
        profilePicture
      } = member;

      return (
        <div className="tribe-approvals__members__member" key={index} data-testid={'tribe-approvals-member'}>
          <div className="tribe-approvals__members__member__details">
            <div className="tribe-approvals__members__member__details__profile-picture">
              {profilePicture && <img src={base64ToImage(profilePicture)} alt="" />}
              {!profilePicture && <UserIcon />}
            </div>
            <p className="tribe-approvals__members__member__details__name">{memberName}</p>
            {postsPendingApproval > 0 && (
              <p className="tribe-approvals__members__member__details__posts">
                {postsPendingApproval} {postsPendingApproval > 1 ? 'Posts' : 'Post'}
              </p>
            )}
            {postsPendingApproval > 0 && commentsPendingApproval > 0 && (
              <p className="tribe-approvals__members__member__details__divider">·</p>
            )}
            {commentsPendingApproval > 0 && (
              <p className="tribe-approvals__members__member__details__comments">
                {commentsPendingApproval} {commentsPendingApproval > 1 ? 'Comments' : 'Comment'}
              </p>
            )}
          </div>
          <DotsIcon
            className="tribe-approvals__members__member__dots-icon"
            onClick={() => handleMemberClick(systemUserExternalReference)}
            data-testid={'tribe-approvals-member-options'}
          />
          {selectedSystemUserExternalReference === systemUserExternalReference && (
            <div className="tribe-approvals__members__member__options">
              {postsPendingApproval > 0 && <p onClick={handleApproveAllPostsClick}>Approve all posts</p>}
              {postsPendingApproval > 0 && <p onClick={handleDeclineAllPostsClick}>Decline all posts</p>}
              {commentsPendingApproval > 0 && <p onClick={handleApproveAllCommentsClick}>Approve all comments</p>}
              {commentsPendingApproval > 0 && <p onClick={handleDeclineAllCommentsClick}>Decline all comments</p>}
              <p onClick={handleViewAllClick}>View all by {memberName}</p>
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <ProgrammeLayout>
      <div className="tribe-approvals" data-testid={`tribe-approvals-${status}`}>
        <div className="tribe-approvals__header">
          <ArrowIcon className="tribe-approvals__header__arrow-icon" onClick={navigateBack} data-testid="arrow-icon" />
          <h1>Posts/Comments Awaiting Approval</h1>
        </div>
        <div className="tribe-approvals__content">
          <div className="tribe-approvals__search">
            <SearchIcon className="tribe-approvals__search__icon" />
            <input
              className="tribe-approvals__search__input"
              value={searchString}
              onChange={({ target }) => setSearchString(target.value)}
              data-testid={'tribe-approvals-search'}
            />
          </div>

          <div className="tribe-approvals__members-header">
            <p>Members</p>
          </div>

          <div className="tribe-approvals__posts">
            <div className="tribe-approvals__posts__filter" data-testid={'tribe-approvals-filter'}>
              <p>See</p>
              <Dropdown
                name="post-sort-dropdown"
                value={filter}
                options={filterOptions}
                onChange={(option) => setFilter(option)}
                inline
              />
            </div>
            {posts.map((post, index) => (
              <TribePostContainer key={index} post={post} status={status} approval />
            ))}
          </div>

          <div className="tribe-approvals__members">{renderMembers()}</div>
        </div>
      </div>
    </ProgrammeLayout>
  );
};

export default TribeApprovals;
